import apiClient from '../api/client';
import { ApiResponse } from '../../types/api.types';

export interface PublicBlog {
  id: number;
  title: string;
  description: string;
  content: string;
  category: string;
  author: string;
  image: string | null;
  tags: string[];
  seo_keywords: string[];
  read_time: number;
  status: string;
  featured: boolean | number;
  slug: string;
  meta_title: string | null;
  meta_description: string | null;
  published_by: string | null;
  published_at: string | null;
  views: number;
  likes: number;
  comments: number;
  rating: number;
  created_at: string;
  updated_at: string | null;
}

export interface PublicBlogListResponse {
  blogs: PublicBlog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics?: {
    draft: number;
    published: number;
    featured: number;
    total: number;
  };
}

export interface GetPublicBlogsParams {
  page?: number;
  limit?: number;
  category?: string;
  featured?: boolean | string;
  search?: string;
  sort?: 'latest' | 'oldest' | 'popular' | 'rating' | 'featured';
}

export interface SearchBlogsParams {
  q: string;
  page?: number;
  limit?: number;
}

export interface BlogStats {
  totalBlogs: number;
  totalViews: number;
  averageRating: string;
  totalLikes: number;
}

class BlogService {
  /**
   * Parse blog tags and seo_keywords
   */
  private parseBlog(blog: any): PublicBlog {
    return {
      ...blog,
      tags: typeof blog.tags === 'string' ? JSON.parse(blog.tags || '[]') : blog.tags || [],
      seo_keywords: typeof blog.seo_keywords === 'string' ? JSON.parse(blog.seo_keywords || '[]') : blog.seo_keywords || [],
    };
  }

  /**
   * Make public API call (with optional auth header)
   * Public endpoints accept optional authentication
   */
  private async makePublicCall<T>(url: string, config: any = {}): Promise<T> {
    try {
      const response = await apiClient.get<ApiResponse<T>>(url, config);
      
      if (!response.data || !response.data.data) {
        throw new Error('Invalid API response structure');
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('Public API call failed:', url, error);
      throw error;
    }
  }

  /**
   * Get All Published Blogs (Public)
   * GET /api/blogs
   */
  async getPublishedBlogs(params: GetPublicBlogsParams = {}): Promise<PublicBlogListResponse> {
    try {
      const data = await this.makePublicCall<PublicBlogListResponse>(
        '/api/blogs',
        { params }
      );
      
      if (data && data.blogs) {
        data.blogs = data.blogs.map((blog: any) => this.parseBlog(blog));
      }
      
      return data;
    } catch (error: any) {
      console.error('Error in getPublishedBlogs:', error);
      throw error;
    }
  }

  /**
   * Get Featured Blogs (Public)
   * GET /api/blogs/featured
   */
  async getFeaturedBlogs(): Promise<PublicBlog[]> {
    const blogs = await this.makePublicCall<PublicBlog[]>(
      '/api/blogs/featured'
    );
    
    return blogs.map((blog: any) => this.parseBlog(blog));
  }

  /**
   * Get Blog Categories (Public)
   * GET /api/blogs/categories
   */
  async getBlogCategories(): Promise<string[]> {
    return await this.makePublicCall<string[]>('/api/blogs/categories');
  }

  /**
   * Search Blogs (Public)
   * GET /api/blogs/search
   */
  async searchBlogs(params: SearchBlogsParams): Promise<PublicBlogListResponse> {
    const data = await this.makePublicCall<PublicBlogListResponse>(
      '/api/blogs/search',
      { params }
    );
    
    data.blogs = data.blogs.map((blog: any) => this.parseBlog(blog));
    
    return data;
  }

  /**
   * Get Blog Statistics (Public)
   * GET /api/blogs/stats
   */
  async getBlogStats(): Promise<BlogStats> {
    return await this.makePublicCall<BlogStats>('/api/blogs/stats');
  }

  /**
   * Get Published Blog By ID (Public)
   * GET /api/blogs/:id
   */
  async getPublishedBlogById(id: number): Promise<PublicBlog> {
    const blog = await this.makePublicCall<PublicBlog>(`/api/blogs/${id}`);
    return this.parseBlog(blog);
  }

  /**
   * Get Published Blog By Slug (Public)
   * GET /api/blogs/slug/:slug
   */
  async getPublishedBlogBySlug(slug: string): Promise<PublicBlog> {
    const blog = await this.makePublicCall<PublicBlog>(`/api/blogs/slug/${slug}`);
    return this.parseBlog(blog);
  }
}

export default new BlogService();
